import { useMemo } from "react";

import {
	BriefcaseIcon,
	MapPinIcon,
	OutlinedDocumentIcon,
	VerticalFiltersIcon,
} from "../../../assets/icons/interface/interface";
import ProfileAvatar from "../../../components/ProfileAvatar";
import { capitalizeFirstLetterOfWords } from "../../../handlers/utilityFunctions";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { useUserAccount } from "../../../services/create-resume/queries";
import { Button } from "../../JobsPage/components/Button";
import { Card, CardContent } from "../../JobsPage/components/Card";

interface ProfileCardProps {
	onEditPreferences: () => void;
	setUploadResumeModal: (open: boolean) => void;
	smartProfileData?: Record<string, unknown>;
}

// Some accounts store these as a comma-joined string rather than an array — show them either way.
function toArray(value: unknown): string[] {
	if (Array.isArray(value)) return value;

	if (typeof value !== "string") return [];

	return value
		.split(",")
		.map((item) => item.trim())
		.filter(Boolean);
}

const ProfileCard = ({ onEditPreferences, setUploadResumeModal, smartProfileData }: ProfileCardProps) => {
	const showDSP = useFeatureFlag("dsp");
	const savedProfile = JSON.parse(localStorage.getItem("profileData") || "{}");

	const { data: userAccount, isLoading } = useUserAccount();

	const profileData = useMemo(() => {
		// Read each preference straight off the account rather than gating the whole block behind a
		// completeness score — the three onboarding answers should show up immediately.
		const preferences = userAccount?.jobPreferences || {};
		const address = preferences.address;

		return {
			name: userAccount?.firstName || savedProfile?.firstName || "",
			picture: userAccount?.picture || savedProfile?.picture || "",
			jobTitle: preferences.desiredTitle || "",
			location: address?.city ? [address.city, address.country].filter(Boolean).join(", ") : "",
			status: preferences.currentJobsearchStatus || "",
			seniorityLevel: preferences.seniorityLevel || "",
			skills: toArray(preferences.skills),
			preferredJobTypes: toArray(preferences.openForWorkTypes),
			preferredIndustries: toArray(preferences.areasOfExpertise),
			completenessPercentage: userAccount?.job_preference_progress_status?.completeness_pct || 0,
		};
	}, [userAccount, savedProfile]);

	if (isLoading) {
		return (
			<Card className="w-full">
				<CardContent className="p-6">
					<div className="flex items-center justify-center">
						<span className="text-gray-500">Loading...</span>
					</div>
				</CardContent>
			</Card>
		);
	}

	const { name, location, jobTitle, seniorityLevel, skills, preferredJobTypes, preferredIndustries } = profileData;

	const chipSections = [
		{ title: "Skills", items: skills },
		{ title: "Seniority Level", items: seniorityLevel ? [seniorityLevel] : [] },
		{ title: "Preferred Job Types", items: preferredJobTypes },
		{ title: "Preferred Industries", items: preferredIndustries },
	].filter((section) => section.items.length > 0);

	return (
		<Card className="w-full">
			{/* Profile Strength Indicator */}
			<div className="p-6 border-b border-primary-200 bg-blue-50/50">
				<div className="flex items-center justify-between mb-2">
					<span className="text-sm font-medium text-primary-600">Profile Strength</span>
					<span className="text-sm font-medium text-gray-900">{profileData.completenessPercentage}%</span>
				</div>
				<div className="w-full bg-gray-200 rounded-full h-1.5">
					<div
						className="bg-primary-600 h-1.5 rounded-full"
						style={{ width: `${profileData.completenessPercentage}%` }}
					/>
				</div>
			</div>

			<CardContent className="p-6">
				<div className="flex items-start">
					{/* Profile Image */}
					<div className="mr-2 md:mr-6">
						<div className="rounded-full border border-gray-200 p-1">
							<ProfileAvatar
								src={profileData.picture}
								name={name}
								className="rounded-full object-cover h-[40px] w-[40px] md:h-[120px] md:w-[120px]"
							/>
						</div>
					</div>
					<div className="grow">
						<h1 className="text-2xl font-bold font-plus-jakarta text-gray-800 mb-1">
							Welcome, {capitalizeFirstLetterOfWords(name || "User")}
						</h1>
						{/* Job title and location with icons */}
						<div className="flex flex-col md:flex-row text-sm text-gray-700 font-inter">
							<span className="flex items-center">
								<BriefcaseIcon className="w-4 h-4 mr-1" />
								{!jobTitle && <span className="w-36 h-4 bg-gray-900 opacity-10 rounded-md" />}
								{jobTitle && <span>{jobTitle}</span>}
							</span>
							<span className="hidden md:block mx-2">•</span>
							<span className="flex items-center">
								<MapPinIcon className="w-4 h-4 mr-1" />
								{!location && <span className="w-36 h-4 bg-gray-900 opacity-10 rounded-md" />}
								{location && <span>{location}</span>}
							</span>
						</div>

						{showDSP && (
							<div className="grid grid-cols-1 md:grid-cols-2 gap-[16px] mt-[32px]">
								<div className="flex flex-col items-center justify-center border rounded-[8px] py-[16px] px-[40px] space-y-[16px]">
									<OutlinedDocumentIcon />
									<div className="text-[14px] font-semibold">Dproz Smart Profile (DSP)</div>
									<Button
										variant="outline"
										className="w-full"
										onClick={() => {
											setUploadResumeModal(true);
										}}
									>
										{Object.keys(smartProfileData || {}).length > 0 ? "Create Another DSP" : "Create DSP"}
									</Button>
								</div>
								<div className="flex flex-col items-center justify-center border rounded-[8px] py-[16px] px-[40px] space-y-[16px]">
									<VerticalFiltersIcon />
									<div className="text-[14px] font-semibold">Job Preferences</div>
									<Button variant="outline" className="w-full" onClick={onEditPreferences}>
										Edit Preferences
									</Button>
								</div>
							</div>
						)}

						{showDSP && (
							<div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-0 border rounded-[8px] px-[12px] py-[18px] mt-[16px]">
								<div className="flex flex-col items-center justify-center py-[6px] space-y[8px] border-r-0 md:border-r">
									<div className="text-[24px] font-bold">0</div>
									<div className="text-[14px] font-normal">Applications</div>
								</div>
								<div className="flex flex-col items-center justify-center py-[6px] space-y[8px] border-r-0 md:border-r">
									<div className="text-[24px] font-bold">0</div>
									<div className="text-[14px] font-normal">Interviews</div>
								</div>
								<div className="flex flex-col items-center justify-center py-[6px] space-y[8px] border-r-0 md:border-r">
									<div className="text-[24px] font-bold">0</div>
									<div className="text-[14px] font-normal">Profile Views</div>
								</div>
								<div className="flex flex-col items-center justify-center py-[6px] space-y[8px]">
									<div className="text-[24px] font-bold">0</div>
									<div className="text-[14px] font-normal">Saved Jobs</div>
								</div>
							</div>
						)}

						{/* Open to Opportunities Label */}
						{profileData.status === "Open to Opportunities" && (
							<span className="inline-block bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full mb-3">
								Open to Opportunities
							</span>
						)}

						{/* Whatever the user has told us so far — onboarding fills in seniority and industries. */}
						{!showDSP &&
							chipSections.map((section) => (
								<div key={section.title} className="mt-4">
									<h2 className="text-sm font-semibold text-gray-700 font-inter mb-2">{section.title}</h2>
									<div className="flex flex-wrap gap-2">
										{section.items.map((item) => (
											<span key={item} className="bg-gray-100 text-gray-700 text-xs font-inter px-2 py-1 rounded">
												{item}
											</span>
										))}
									</div>
								</div>
							))}

						{!showDSP && (
							<Button variant="outline" className="w-full mt-8" onClick={onEditPreferences}>
								Edit Preferences
							</Button>
						)}
					</div>
				</div>
			</CardContent>
		</Card>
	);
};

export default ProfileCard;
